import { type GeometryObject } from 'geojson'
import { environment } from '../../../../environments/environment'
import { type CartoClient } from './carto.helper'

export interface PoiDetailRow {
  // Geometry (from QUADBIN_BOUNDARY)
  geom: GeometryObject

  // POI attributes (from pois table)
  poi_id: string
  poi_latitude: number
  poi_longitude: number
  poi_name: string
  poi_address: string
  poi_municipality: string
  poi_area: string
  poi_streets: string[]
  quadkey_id: string

  // Metrics dimensions (from poi_metrics table)
  delivery_id: number
  delivery_start_date: string
  filter_combined: string
  poi_tag_ids: number[]

  // Core metrics
  nvisitors: number
  nvisits: number
  traffic: number

  // Gender demographics
  nvisitors_gender_m: number
  nvisitors_gender_f: number
  nvisitors_gender_unknown: number

  // Age demographics
  nvisitors_age_18_24: number
  nvisitors_age_25_34: number
  nvisitors_age_35_44: number
  nvisitors_age_45_54: number
  nvisitors_age_55_64: number
  nvisitors_age_65_100: number
  nvisitors_age_unknown: number

  // Visitor profiles
  nvisitors_visitor_profile_local: number
  nvisitors_visitor_profile_regional: number
  nvisitors_visitor_profile_national: number
  nvisitors_visitor_profile_international: number
  nvisitors_visitor_profile_resident: number
  nvisitors_visitor_profile_commuter: number
  nvisitors_visitor_profile_unknown: number

  // Global aggregated metrics (across all POIs in delivery)
  minnvisitors: number
  maxnvisitors: number
  countnvisitors: number
  avgnvisitors: number
  sumnvisitors: number
  mintraffic: number
  maxtraffic: number
  counttraffic: number
  avgtraffic: number
  sumtraffic: number
}

export interface PoiInfoFilters {
  poi_id: string
  delivery_id: number
  delivery_start_date: string
  filter_combined: string
}

export interface PoiCompareDeliveryFilters {
  poi_id: string
  delivery_id_1: number
  delivery_id_2: number
  delivery_start_date_1: string
  delivery_start_date_2: string
  filter_combined: string
}

export async function fetchPoiInfo(
  cartoClient: CartoClient,
  filters: PoiInfoFilters,
  signal?: AbortSignal,
): Promise<PoiDetailRow | null> {
  const { poi_id, delivery_id, delivery_start_date, filter_combined } = filters
  const response = await cartoClient.query<PoiDetailRow>({
    source: {
      source: `${environment.CARTO_SOURCE_PREFIX}_poi_detail`,
      queryParameters: {
        poi_id,
        delivery_id,
        delivery_start_date,
        filter_combined,
      },
    },
    params: {
      column: [
        'poi_id',
        'geom',
        'poi_latitude',
        'poi_longitude',
        'poi_name',
        'poi_address',
        'poi_municipality',
        'poi_area',
        'poi_streets',
        'quadkey_id',
        'delivery_id',
        'delivery_start_date',
        'filter_combined',
        'poi_tag_ids',
        'nvisitors',
        'nvisits',
        'traffic',
        'nvisitors_gender_m',
        'nvisitors_gender_f',
        'nvisitors_gender_unknown',
        'nvisitors_age_18_24',
        'nvisitors_age_25_34',
        'nvisitors_age_35_44',
        'nvisitors_age_45_54',
        'nvisitors_age_55_64',
        'nvisitors_age_65_100',
        'nvisitors_age_unknown',
        'nvisitors_visitor_profile_local',
        'nvisitors_visitor_profile_regional',
        'nvisitors_visitor_profile_national',
        'nvisitors_visitor_profile_international',
        'nvisitors_visitor_profile_resident',
        'nvisitors_visitor_profile_commuter',
        'nvisitors_visitor_profile_unknown',
        'minnvisitors',
        'maxnvisitors',
        'countnvisitors',
        'avgnvisitors',
        'sumnvisitors',
        'mintraffic',
        'maxtraffic',
        'counttraffic',
        'avgtraffic',
        'sumtraffic',
      ],
    },
    signal,
  })

  return response?.rows?.[0] ?? null
}

export async function fetchPoiCompareDeliveryInfo(
  cartoClient: CartoClient,
  filters: PoiCompareDeliveryFilters,
  signal?: AbortSignal,
): Promise<PoiDetailRow[]> {
  const {
    poi_id,
    delivery_id_1,
    delivery_id_2,
    delivery_start_date_1,
    delivery_start_date_2,
    filter_combined,
  } = filters
  const response = await cartoClient.query<PoiDetailRow>({
    source: {
      source: `${environment.CARTO_SOURCE_PREFIX}_poi_compare_delivery_detail`,
      queryParameters: {
        poi_id,
        delivery_id_1,
        delivery_id_2,
        delivery_start_date_1,
        delivery_start_date_2,
        filter_combined,
      },
    },
    params: {
      column: [
        'poi_id',
        'geom',
        'poi_latitude',
        'poi_longitude',
        'poi_name',
        'poi_address',
        'poi_municipality',
        'poi_area',
        'poi_streets',
        'quadkey_id',
        'delivery_id',
        'delivery_start_date',
        'filter_combined',
        'poi_tag_ids',
        'nvisitors',
        'nvisits',
        'traffic',
        'nvisitors_gender_m',
        'nvisitors_gender_f',
        'nvisitors_gender_unknown',
        'nvisitors_age_18_24',
        'nvisitors_age_25_34',
        'nvisitors_age_35_44',
        'nvisitors_age_45_54',
        'nvisitors_age_55_64',
        'nvisitors_age_65_100',
        'nvisitors_age_unknown',
        'nvisitors_visitor_profile_local',
        'nvisitors_visitor_profile_regional',
        'nvisitors_visitor_profile_national',
        'nvisitors_visitor_profile_international',
        'nvisitors_visitor_profile_resident',
        'nvisitors_visitor_profile_commuter',
        'nvisitors_visitor_profile_unknown',
      ],
    },
    signal,
  })

  return response?.rows ?? []
}
